import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal, Space, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { messageCenterApi } from '@/modules/foundation/api/messageCenter';
import type { AnnouncementInfo, MessageInfo } from '@/modules/foundation/api/types';

const { Paragraph } = Typography;

type PopupItem =
  | { key: string; kind: 'announcement'; data: AnnouncementInfo }
  | { key: string; kind: 'message'; data: MessageInfo };

const AnnouncementPopup: React.FC = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState<PopupItem[]>([]);
  const current = queue[0];

  const load = useCallback(async () => {
    const [announcements, messages] = await Promise.allSettled([
      messageCenterApi.getAnnouncementPopups(),
      messageCenterApi.getMessagePopups(),
    ]);
    const incoming: PopupItem[] = [];
    if (announcements.status === 'fulfilled') {
      incoming.push(...(announcements.value.list || []).map((data) => ({
        key: `announcement-${data.id}`,
        kind: 'announcement' as const,
        data,
      })));
    }
    if (messages.status === 'fulfilled') {
      incoming.push(...(messages.value.list || []).map((data) => ({
        key: `message-${data.id}`,
        kind: 'message' as const,
        data,
      })));
    }
    setQueue((existing) => {
      const keys = new Set(existing.map((item) => item.key));
      return [...existing, ...incoming.filter((item) => !keys.has(item.key))];
    });
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => { void load(); }, 30_000);
    const onFocus = () => { void load(); };
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  useEffect(() => {
    if (!current) return;
    if (current.kind === 'announcement') {
      void messageCenterApi.recordAnnouncementImpression(current.data.id).catch(() => undefined);
    } else {
      void messageCenterApi.recordMessageImpression(current.data.id).catch(() => undefined);
    }
  }, [current]);

  if (!current) return null;

  const item = current.data;
  const requireAck = current.kind === 'announcement' && current.data.requireAck;
  const next = () => setQueue((items) => items.slice(1));
  const dismiss = async () => {
    try {
      if (current.kind === 'announcement') {
        await messageCenterApi.dismissAnnouncement(current.data.id);
      } else {
        await messageCenterApi.dismissMessage(current.data.id);
      }
      next();
    } catch {
      message.error('消息状态更新失败');
    }
  };
  const acknowledge = async () => {
    if (current.kind !== 'announcement') return;
    try {
      await messageCenterApi.acknowledgeAnnouncement(current.data.id);
      next();
    } catch {
      message.error('公告确认失败');
    }
  };
  const openAction = () => {
    if (current.kind === 'message') {
      void Promise.all([
        messageCenterApi.dismissMessage(current.data.id),
        messageCenterApi.markMessagesRead({ ids: [current.data.id] }),
      ]).catch(() => undefined);
    }
    if (item.actionUrl?.startsWith('/')) navigate(item.actionUrl);
    else navigate('/tenant/message-center', current.kind === 'announcement'
      ? { state: { announcementId: current.data.id } }
      : { state: { messageId: current.data.id } });
    next();
  };

  if (current.kind === 'announcement' && current.data.displayMode === 'banner') {
    return (
      <Alert
        banner
        showIcon
        type={current.data.level === 'urgent' ? 'error' : 'warning'}
        message={current.data.title}
        description={current.data.content}
        closable={!requireAck}
        onClose={() => { void dismiss(); }}
        action={requireAck
          ? <Button size="small" type="primary" onClick={() => { void acknowledge(); }}>我已知晓</Button>
          : current.data.actionLabel ? <Button size="small" onClick={openAction}>{current.data.actionLabel}</Button> : undefined}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100 }}
      />
    );
  }

  return (
    <Modal
      open
      title={item.title}
      closable={!requireAck}
      maskClosable={!requireAck}
      keyboard={!requireAck}
      onCancel={() => { void dismiss(); }}
      footer={
        <Space>
          {item.actionLabel && <Button onClick={openAction}>{item.actionLabel}</Button>}
          <Button type="primary" onClick={() => { void (requireAck ? acknowledge() : dismiss()); }}>
            {requireAck ? '我已知晓' : '关闭'}
          </Button>
        </Space>
      }
    >
      <Paragraph style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{item.content}</Paragraph>
    </Modal>
  );
};

export default AnnouncementPopup;
